// src/components/UsersTab.jsx
import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Table, 
  Button, 
  Modal, 
  Form, 
  Alert, 
  Spinner, 
  Badge,
  Pagination,
  InputGroup,
  Row,
  Col,
  Collapse
} from 'react-bootstrap';
import { usersAPI } from '../services/api.js';
import CreateUserModal from './CreateUserModal';

const UsersTab = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [expandedUser, setExpandedUser] = useState(null);
  const [editingUser, setEditingUser] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Состояния для модального окна создания пользователя
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const usersPerPage = 10;

  useEffect(() => {
    loadUsers();
  }, [currentPage, refreshTrigger]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = await usersAPI.getUsers({
        skip: (currentPage - 1) * usersPerPage,
        limit: usersPerPage
      });
      
      // Обрабатываем ответ
      const usersList = Array.isArray(response) ? response : (response.users || response.data || []);
      
      // Обеспечиваем что у всех пользователей есть необходимые поля
      const usersWithDefaults = usersList.map(user => ({
        ...user,
        telegram_first_name: user.telegram_first_name || null,
        telegram_last_name: user.telegram_last_name || null,
        telegram_username: user.telegram_username || null,
        telegram_id: user.telegram_id || null,
        name: user.name || 'Пользователь',
        email: user.email || null,
        is_active: user.is_active !== undefined ? user.is_active : true,
        role: user.role || 'user'
      }));
      
      setUsers(usersWithDefaults);
      
      // Расчет пагинации
      const total = response.total_count || usersWithDefaults.length;
      setTotalPages(Math.ceil(total / usersPerPage));
      
    } catch (err) {
      console.error('Error loading users:', err);
      setError('Не удалось загрузить список пользователей');
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  // Функция обновления списка после создания пользователя
  const handleUserCreated = (newUser) => {
    console.log('✅ User created, refreshing list:', newUser);
    setRefreshTrigger(prev => prev + 1);
    setShowCreateModal(false);
  };

  const toggleExpand = (userId) => {
    if (expandedUser === userId) {
      setExpandedUser(null);
      setEditingUser(null);
    } else {
      setExpandedUser(userId);
      setEditingUser(null);
    }
  };

  const startEdit = (user, e) => {
    e.stopPropagation();
    setEditingUser({ ...user });
    if (expandedUser !== user.id) {
      setExpandedUser(user.id);
    }
  };

  const cancelEdit = (e) => {
    e?.stopPropagation();
    setEditingUser(null);
  };

  const handleSave = async (e) => {
    e?.stopPropagation();
    if (!editingUser) return;

    try {
      setSaving(true);
      setError('');

      await usersAPI.updateUser(editingUser.id, {
        name: editingUser.name,
        email: editingUser.email,
        is_active: editingUser.is_active,
        role: editingUser.role
      });

      setUsers(prevUsers => 
        prevUsers.map(user => 
          user.id === editingUser.id ? editingUser : user
        )
      );

      setEditingUser(null);
    } catch (err) {
      console.error('Error updating user:', err);
      setError('Не удалось обновить пользователя');
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (field, value) => {
    setEditingUser(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleToggleActive = async (userId, currentStatus, e) => {
    e.stopPropagation();
    
    try {
      await usersAPI.updateUser(userId, {
        is_active: !currentStatus
      });

      setUsers(prevUsers => 
        prevUsers.map(user => 
          user.id === userId ? { ...user, is_active: !currentStatus } : user
        )
      );
    } catch (err) {
      console.error('Error toggling user status:', err);
      setError('Не удалось изменить статус пользователя');
    }
  };

  const handleDeleteClick = (user, e) => {
    e.stopPropagation();
    setDeleteConfirm(user);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirm) return;

    try {
      setDeleting(true);
      
      await usersAPI.deleteUser(deleteConfirm.id);

      setUsers(prevUsers => 
        prevUsers.filter(user => user.id !== deleteConfirm.id)
      );

      setDeleteConfirm(null);
      setExpandedUser(null);
      
      if (users.length === 1 && currentPage > 1) {
        setCurrentPage(prev => prev - 1);
      }
    } catch (err) {
      console.error('Error deleting user:', err);
      setError('Не удалось удалить пользователя');
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteConfirm(null);
  };

  // Безопасное получение полного имени
  const getFullName = (user) => {
    if (!user) return 'Не назначен';
    
    if (user.error) {
      return user.name || `Пользователь #${user.id}`;
    }
    
    const firstName = user.telegram_first_name || '';
    const lastName = user.telegram_last_name || '';
    
    if (firstName && lastName) {
      return `${firstName} ${lastName}`;
    } else if (firstName) {
      return firstName;
    } else if (lastName) {
      return lastName;
    }
    
    return user.name || 'Пользователь';
  };

  // Безопасное получение инициалов
  const getInitials = (user) => {
    if (!user) return 'U';
    
    const fullName = getFullName(user);
    return fullName
      .split(' ')
      .map(word => word.charAt(0))
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };

  // Безопасное получение username
  const getUsername = (user) => {
    if (!user) return '';
    return user.telegram_username || user.name || 'user';
  };

  const getRoleBadgeVariant = (role) => {
    switch (role) {
      case 'admin': return 'danger';
      case 'moderator': return 'warning';
      default: return 'secondary';
    }
  };

  const getStatusVariant = (isActive) => {
    return isActive ? 'success' : 'secondary';
  };

  const hasTelegramData = (user) => {
    if (!user) return false;
    return !!(user.telegram_id || user.telegram_username);
  };

  const filteredUsers = users.filter(user => {
    if (!user) return false;
    
    const searchLower = searchTerm.toLowerCase();
    return (
      (user.name && user.name.toLowerCase().includes(searchLower)) ||
      (user.email && user.email.toLowerCase().includes(searchLower)) ||
      (user.telegram_username && user.telegram_username.toLowerCase().includes(searchLower)) ||
      getFullName(user).toLowerCase().includes(searchLower)
    );
  });

  const renderPagination = () => {
    if (totalPages <= 1) return null;
    
    const pages = [];
    const maxVisiblePages = 5;
    
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
    
    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <Pagination.Item
          key={i}
          active={i === currentPage}
          onClick={() => setCurrentPage(i)}
        >
          {i}
        </Pagination.Item>
      );
    }

    return (
      <div className="d-flex justify-content-between align-items-center mt-3">
        <div>
          <span className="text-muted">
            Показано {filteredUsers.length} пользователей на странице {currentPage} из {totalPages}
          </span>
        </div>
        
        <Pagination className="mb-0">
          <Pagination.Prev 
            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
            disabled={currentPage === 1}
          />
          
          {startPage > 1 && (
            <>
              <Pagination.Item onClick={() => setCurrentPage(1)}>1</Pagination.Item>
              {startPage > 2 && <Pagination.Ellipsis />}
            </>
          )}
          
          {pages}
          
          {endPage < totalPages && (
            <>
              {endPage < totalPages - 1 && <Pagination.Ellipsis />}
              <Pagination.Item onClick={() => setCurrentPage(totalPages)}>
                {totalPages}
              </Pagination.Item>
            </>
          )}
          
          <Pagination.Next 
            onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
            disabled={currentPage === totalPages}
          />
        </Pagination>
      </div>
    );
  };

  if (loading && users.length === 0) {
    return (
      <Card>
        <Card.Body className="text-center py-5">
          <Spinner animation="border" variant="primary" className="mb-3" />
          <p className="text-muted">Загрузка пользователей...</p>
        </Card.Body>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <Card.Header className="d-flex justify-content-between align-items-center">
          <h5 className="mb-0">
            <i className="bi bi-people me-2"></i>
            Управление пользователями
          </h5>
          <div className="d-flex gap-2">
            <InputGroup style={{ width: '300px' }}>
              <Form.Control
                placeholder="Поиск по имени, email или username..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <Button variant="outline-secondary">
                <i className="bi bi-search"></i>
              </Button>
            </InputGroup>
            
            {/* Кнопка создания пользователя */}
            <Button 
              variant="primary" 
              onClick={() => setShowCreateModal(true)}
              className="d-flex align-items-center gap-2"
            >
              <i className="bi bi-plus-lg"></i>
              Создать пользователя
            </Button>
            
            <Button variant="outline-primary" onClick={loadUsers}>
              <i className="bi bi-arrow-clockwise"></i>
            </Button>
          </div>
        </Card.Header>

        <Card.Body className="p-0">
          {error && (
            <Alert variant="danger" className="m-3 mb-0">
              {error}
            </Alert>
          )}

          {filteredUsers.length === 0 && !loading ? (
            <div className="text-center py-5">
              <i className="bi bi-people display-1 text-muted opacity-50 mb-3"></i>
              <h5>Пользователи не найдены</h5>
              <p className="text-muted mb-0">
                {searchTerm 
                  ? 'Попробуйте изменить параметры поиска' 
                  : 'Создайте первого пользователя'}
              </p>
              {!searchTerm && (
                <Button 
                  variant="primary" 
                  className="mt-3"
                  onClick={() => setShowCreateModal(true)}
                >
                  <i className="bi bi-plus-lg me-2"></i>
                  Создать пользователя
                </Button>
              )}
            </div>
          ) : (
            <>
              <div className="table-responsive">
                <Table hover className="mb-0">
                  <thead className="table-light">
                    <tr>
                      <th width="50"></th>
                      <th>Пользователь</th>
                      <th>Контакты</th>
                      <th>Роль</th>
                      <th>Статус</th>
                      <th width="120">Действия</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUsers.map(user => (
                      <React.Fragment key={user.id}>
                        {/* Основная строка */}
                        <tr 
                          onClick={() => toggleExpand(user.id)}
                          className="cursor-pointer"
                          style={{ cursor: 'pointer' }}
                        >
                          <td>
                            <i 
                              className={`bi bi-chevron-${expandedUser === user.id ? 'down' : 'right'} text-muted`}
                            ></i>
                          </td>
                          <td>
                            <div className="d-flex align-items-center">
                              <div 
                                className="bg-primary rounded-circle d-flex align-items-center justify-content-center me-3 text-white fw-bold"
                                style={{ width: '40px', height: '40px', fontSize: '0.9rem' }}
                              >
                                {getInitials(user)}
                              </div>
                              <div>
                                <div className="fw-semibold">{getFullName(user)}</div>
                                <div className="text-muted small">@{getUsername(user)}</div>
                              </div>
                            </div>
                          </td>
                          <td>
                            <div>
                              <div className="fw-semibold">{user.email || 'Не указан'}</div>
                              {user.telegram_username && (
                                <div className="text-muted small">
                                  <i className="bi bi-telegram me-1"></i>
                                  @{user.telegram_username}
                                </div>
                              )}
                            </div>
                          </td>
                          <td>
                            <Badge bg={getRoleBadgeVariant(user.role)}>
                              {user.role === 'admin' ? 'Админ' : 
                               user.role === 'moderator' ? 'Модератор' : 'Пользователь'}
                            </Badge>
                          </td>
                          <td>
                            <Button
                              variant={getStatusVariant(user.is_active)}
                              size="sm"
                              onClick={(e) => handleToggleActive(user.id, user.is_active, e)}
                              className="w-100"
                            >
                              {user.is_active ? 'Активен' : 'Неактивен'}
                            </Button>
                          </td>
                          <td>
                            <div className="d-flex gap-1">
                              <Button
                                variant="outline-primary"
                                size="sm"
                                onClick={(e) => startEdit(user, e)}
                                title="Редактировать"
                              >
                                <i className="bi bi-pencil"></i>
                              </Button>
                              <Button
                                variant="outline-danger"
                                size="sm"
                                onClick={(e) => handleDeleteClick(user, e)}
                                title="Удалить"
                              >
                                <i className="bi bi-trash"></i>
                              </Button>
                            </div>
                          </td>
                        </tr>

                        {/* Расширенная информация */}
                        <tr>
                          <td colSpan={6} className="p-0 border-0">
                            <Collapse in={expandedUser === user.id}>
                              <div className="bg-light">
                                <div className="p-3">
                                  {editingUser?.id === user.id ? (
                                    // Режим редактирования
                                    <div className="bg-white rounded p-3 border">
                                      <h6 className="mb-3">
                                        <i className="bi bi-pencil-square me-2 text-primary"></i>
                                        Редактирование пользователя
                                      </h6>
                                      <Row className="g-3">
                                        <Col md={6}>
                                          <Form.Group>
                                            <Form.Label>Полное имя</Form.Label>
                                            <Form.Control
                                              type="text"
                                              value={editingUser.name || ''}
                                              onChange={(e) => handleInputChange('name', e.target.value)}
                                              placeholder="Введите имя"
                                            />
                                          </Form.Group>
                                        </Col>
                                        <Col md={6}>
                                          <Form.Group>
                                            <Form.Label>Email</Form.Label>
                                            <Form.Control
                                              type="email"
                                              value={editingUser.email || ''}
                                              onChange={(e) => handleInputChange('email', e.target.value)}
                                              placeholder="Введите email"
                                            />
                                          </Form.Group>
                                        </Col>
                                        <Col md={6}>
                                          <Form.Group>
                                            <Form.Label>Роль</Form.Label>
                                            <Form.Select
                                              value={editingUser.role || 'user'}
                                              onChange={(e) => handleInputChange('role', e.target.value)}
                                            >
                                              <option value="user">Пользователь</option>
                                              <option value="moderator">Модератор</option>
                                              <option value="admin">Администратор</option>
                                            </Form.Select>
                                          </Form.Group>
                                        </Col>
                                        <Col md={6}>
                                          <Form.Group>
                                            <Form.Label>Статус</Form.Label>
                                            <Form.Select
                                              value={editingUser.is_active ? 'active' : 'inactive'}
                                              onChange={(e) => handleInputChange('is_active', e.target.value === 'active')}
                                            >
                                              <option value="active">Активен</option>
                                              <option value="inactive">Неактивен</option>
                                            </Form.Select>
                                          </Form.Group>
                                        </Col>
                                        <Col xs={12}>
                                          <div className="d-flex gap-2 justify-content-end border-top pt-3">
                                            <Button
                                              variant="success"
                                              onClick={handleSave}
                                              disabled={saving}
                                            >
                                              {saving ? (
                                                <>
                                                  <Spinner animation="border" size="sm" className="me-2" />
                                                  Сохранение...
                                                </>
                                              ) : (
                                                <>
                                                  <i className="bi bi-check-lg me-2"></i>
                                                  Сохранить
                                                </>
                                              )}
                                            </Button>
                                            <Button
                                              variant="secondary"
                                              onClick={cancelEdit}
                                            >
                                              <i className="bi bi-x-lg me-2"></i>
                                              Отмена
                                            </Button>
                                          </div>
                                        </Col>
                                      </Row>
                                    </div>
                                  ) : (
                                    // Режим просмотра
                                    <Row className="g-4">
                                      <Col md={6}>
                                        <Card className="h-100">
                                          <Card.Header className="bg-white">
                                            <h6 className="mb-0">
                                              <i className="bi bi-info-circle me-2"></i>
                                              Основная информация
                                            </h6>
                                          </Card.Header>
                                          <Card.Body>
                                            <div className="mb-2">
                                              <small className="text-muted">ID пользователя</small>
                                              <div className="fw-semibold">#{user.id}</div>
                                            </div>
                                            <div className="mb-2">
                                              <small className="text-muted">Системное имя</small>
                                              <div className="fw-semibold">{user.name || 'Не указано'}</div>
                                            </div>
                                            <div className="mb-2">
                                              <small className="text-muted">Полное имя</small>
                                              <div className="fw-semibold">{getFullName(user)}</div>
                                            </div>
                                            {user.telegram_id && (
                                              <div className="mb-2">
                                                <small className="text-muted">Telegram ID</small>
                                                <div className="fw-semibold">{user.telegram_id}</div>
                                              </div>
                                            )}
                                          </Card.Body>
                                        </Card>
                                      </Col>
                                      <Col md={6}>
                                        <Card className="h-100">
                                          <Card.Header className="bg-white">
                                            <h6 className="mb-0">
                                              <i className="bi bi-telephone me-2"></i>
                                              Контактная информация
                                            </h6>
                                          </Card.Header>
                                          <Card.Body>
                                            <div className="mb-2">
                                              <small className="text-muted">Email</small>
                                              <div className="fw-semibold">{user.email || 'Не указан'}</div>
                                            </div>
                                            {user.telegram_username && (
                                              <div className="mb-2">
                                                <small className="text-muted">Telegram</small>
                                                <div className="fw-semibold">
                                                  <i className="bi bi-telegram me-1 text-primary"></i>
                                                  @{user.telegram_username}
                                                </div>
                                              </div>
                                            )}
                                            {user.telegram_first_name && (
                                              <div className="mb-2">
                                                <small className="text-muted">Имя в Telegram</small>
                                                <div className="fw-semibold">{user.telegram_first_name}</div>
                                              </div>
                                            )}
                                            {user.telegram_last_name && (
                                              <div className="mb-2">
                                                <small className="text-muted">Фамилия в Telegram</small>
                                                <div className="fw-semibold">{user.telegram_last_name}</div>
                                              </div>
                                            )}
                                          </Card.Body>
                                        </Card>
                                      </Col>
                                    </Row>
                                  )}
                                </div>
                              </div>
                            </Collapse>
                          </td>
                        </tr>
                      </React.Fragment>
                    ))}
                  </tbody>
                </Table>
              </div>
              
              {/* Пагинация */}
              {renderPagination()}
            </>
          )}
        </Card.Body>
      </Card>

      {/* Модальное окно создания пользователя */}
      <CreateUserModal
        show={showCreateModal}
        onHide={() => setShowCreateModal(false)}
        onUserCreated={handleUserCreated}
      />

      {/* Модальное окно подтверждения удаления */}
      <Modal show={!!deleteConfirm} onHide={handleDeleteCancel} centered>
        <Modal.Header closeButton>
          <Modal.Title className="text-danger">
            <i className="bi bi-exclamation-triangle me-2"></i>
            Подтверждение удаления
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {deleteConfirm && (
            <>
              <p>
                Вы уверены, что хотите удалить пользователя 
                <strong> "{getFullName(deleteConfirm)}"</strong>?
              </p>
              <Alert variant="danger">
                <i className="bi bi-exclamation-triangle me-2"></i>
                Это действие невозможно отменить. Все данные пользователя будут удалены.
              </Alert>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleDeleteCancel}>
            Отмена
          </Button>
          <Button variant="danger" onClick={handleDeleteConfirm} disabled={deleting}>
            {deleting ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Удаление...
              </>
            ) : (
              <>
                <i className="bi bi-trash me-2"></i>
                Удалить
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default UsersTab;